/**
 * Lightweight Event Manager
 *
 * A minimal, high-performance event manager with:
 * - Multiple logger support (Observer pattern)
 * - Custom logger strategies (Strategy pattern)
 * - Automatic batching with configurable flush
 * - Non-blocking, fire-and-forget operations, use flushEvents()
 * - Blocking, If need to ensure events are sent, use flushEventsAsync()
 * - Full error isolation (never breaks UI)
 */

import { IEvent, ILogger, ILoggerConfig, IEventOptions } from './types';

/** Check if running in development mode */
const isDevelopment = (): boolean => {
	try {
		return process.env.NODE_ENV !== 'production';
	} catch {
		return false;
	}
};

/** Safe wrapper - catches all errors */
const safe = <T>(fn: () => T, fallback?: T): T | undefined => {
	try {
		return fn();
	} catch (error) {
		// Log errors in development for debugging
		if (isDevelopment()) {
			console.error('[EventManager] Error in safe wrapper:', error);
		}
		return fallback;
	}
};

/** Safe async wrapper - catches sync and async errors, never throws */
const safeAsync = async (fn: () => Promise<void>): Promise<void> => {
	try {
		await fn();
	} catch (error) {
		// Log errors in development for debugging
		if (isDevelopment()) {
			console.error('[EventManager] Error in safeAsync wrapper:', error);
		}
	}
};

/** Logger wrapper with batching */
class LoggerWrapper<T extends ILoggerConfig> {
	private queue: IEvent[] = [];
	private timer: ReturnType<typeof setTimeout> | null = null;
	private ready = false;

	constructor(public logger: ILogger<T>) {
		safe(() => {
			this.logger.init?.();
			this.ready = true;
		});
	}

	get id() {
		return this.logger.config.id;
	}
	get enabled() {
		return this.logger.config.enabled !== false;
	}
	get batchSize() {
		return this.logger.config.batchSize ?? 10;
	}
	get flushMs() {
		return this.logger.config.flushMs ?? 5000;
	}
	get bypassBatching() {
		return this.logger.config?.bypassBatching === true;
	}

	add(event: IEvent, immediate?: boolean): void {
		if (!this.ready || !this.enabled) return;

		const processed = safe(() =>
			this.logger.process ? this.logger.process(event) : event,
		);
		if (!processed) return;
		if (this.bypassBatching && this.queue.length) {
			this.flush();
		}

		if (immediate || this.bypassBatching) {
			safeAsync(() => this.logger.send([processed]));
			return;
		}

		this.queue.push(processed);
		if (this.queue.length >= this.batchSize) {
			this.flush();
		} else this.timer ??= setTimeout(() => this.flush(), this.flushMs);
	}

	flush(): void {
		if (!this.queue.length) return;
		const events = this.queue;
		this.queue = [];
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
		safeAsync(() => this.logger.send(events));
	}

	/** Async flush - waits for send to complete, errors are caught and logged */
	async flushAsync(): Promise<void> {
		if (!this.queue.length) return;
		const events = this.queue;
		this.queue = [];
		if (this.timer) {
			clearTimeout(this.timer);
			this.timer = null;
		}
		try {
			await this.logger.send(events);
		} catch (error) {
			if (isDevelopment()) {
				console.error('[EventManager] flushAsync send failed:', error);
			}
			// Don't rethrow - maintain same error isolation as flush()
		}
	}

	destroy(): void {
		this.flush();
		safe(() => this.logger.destroy?.());
		this.ready = false;
	}
}

/** Singleton Event Manager */
export class EventManager {
	// eslint-disable-next-line no-use-before-define
	private static instance: EventManager | null = null;
	private readonly loggers = new Map<string, LoggerWrapper<ILoggerConfig>>();

	static get(): EventManager {
		return (
			EventManager.instance ?? (EventManager.instance = new EventManager())
		);
	}

	static reset(): void {
		EventManager.instance?.destroy();
		EventManager.instance = null;
	}

	register<T extends ILoggerConfig>(logger: ILogger<T>): this {
		if (!this.loggers.has(logger.config.id)) {
			this.loggers.set(logger.config.id, new LoggerWrapper(logger));
		}
		return this;
	}

	unregister(id: string): this {
		this.loggers.get(id)?.destroy();
		this.loggers.delete(id);
		return this;
	}

	/**
	 * Add event to queue (batched, non-blocking, never throws)
	 * Matches mercury-ui-v3: addEvent(eventName, eventDetails?, eventTime?)
	 */
	addEvent(
		eventName: string,
		data?: Record<string, unknown>,
		opts?: IEventOptions,
	): void {
		const event: IEvent = { name: eventName, time: Date.now(), data };
		for (const w of this.getTargets(opts)) {
			safe(() => w.add(event, opts?.immediate));
		}
	}

	/**
	 * Add event with delay (non-blocking, never throws)
	 * Matches mercury-ui-v3: addEventWithDelay(eventName, eventDetails?, eventTime?)
	 */
	addEventWithDelay(
		eventName: string,
		data?: Record<string, unknown>,
		delayMs = 5000,
		opts?: IEventOptions,
	): void {
		setTimeout(() => this.addEvent(eventName, data, opts), delayMs);
	}

	/**
	 * Push critical events immediately (non-blocking, fire-and-forget)
	 * Matches mercury-ui-v3: pushCriticalEvents(eventName, eventDetails?, eventTime?)
	 */
	pushCriticalEvents(
		eventName: string,
		data?: Record<string, unknown>,
		opts?: IEventOptions,
	): void {
		this.addEvent(eventName, data, { ...opts, immediate: true });
	}

	/**
	 * Flush all logger queues
	 * Matches mercury-ui-v3: flushEvents()
	 */
	flushEvents(): void {
		for (const w of this.loggers.values()) safe(() => w.flush());
	}

	/**
	 * Async flush - waits for all loggers to complete sending
	 * Each logger flush has its own error handling, so individual failures don't break the overall flush
	 * @returns Promise that resolves when all loggers have attempted to flush
	 */
	async flushEventsAsync(): Promise<void> {
		const promises = [...this.loggers.values()]
			.filter((w) => w.enabled)
			.map((w) => w.flushAsync());
		await Promise.all(promises);
	}

	/** Enable/disable a logger */
	setEnabled(id: string, enabled: boolean): void {
		const w = this.loggers.get(id);
		if (w) w.logger.config.enabled = enabled;
	}

	/** Get a logger */
	get<T extends ILoggerConfig>(id: string): ILogger<T> | undefined {
		return this.loggers.get(id)?.logger as ILogger<T>;
	}

	/** Destroy all */
	destroy(): void {
		for (const w of this.loggers.values()) safe(() => w?.destroy());
		this.loggers.clear();
	}

	private getTargets(opts?: IEventOptions): LoggerWrapper<ILoggerConfig>[] {
		let targets = [...this.loggers.values()].filter((w) => w.enabled);
		if (opts?.only?.length) {
			targets = targets.filter((w) => opts.only!.indexOf(w.id) !== -1);
		}
		if (opts?.skip?.length) {
			targets = targets.filter((w) => opts.skip!.indexOf(w.id) === -1);
		}
		return targets;
	}
}

/** Convenience function to get the EventManager instance */
export const getEventManager = (): EventManager => EventManager.get();
